import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { settingsAPI, authAPI, peoplePlatformAPI, ledgerPlatformAPI } from '@/services';
import { mapPeopleListResponse } from '../models/memberModel';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import PINInput from '../components/PINInput';
import { ENABLE_EMBEDDED_SAAS_UI, ADMIN_CONSOLE_URL, adminConsoleUrl } from '../config/platform';
import { normalizeExceptionalTypeKey } from '../utils/exceptionalContributions';
import {
  UI_SURFACE_CATALOG,
  buildUiSurfacesPayload,
  normalizeUiSurfaces,
} from '../utils/uiSurfaceVisibility';
import ModalShell from '../components/ModalShell';

const DEFAULT_FINANCIAL_NOTIFICATIONS = {
  notify_member_pending: true,
  notify_member_approved: true,
  notify_member_rejected: true,
};

/** Payload Entrea uniquement (hors comptes bancaires / moyens de paiement gérés par Ledgera). */
function buildEntreaSettingsPayload(s) {
  const out = {
    organization: s.organization,
    events: s.events,
    system: (() => {
      const sys = s.system && typeof s.system === 'object' ? { ...s.system } : {};
      sys.ui_surfaces = buildUiSurfacesPayload(sys.ui_surfaces);
      return sys;
    })(),
    signatories: s.signatories || [],
    assistance: {
      default_case_amount: Math.max(0, Number(s.assistance?.default_case_amount) || 0),
      governance: {
        fund_type: s.assistance?.governance?.fund_type || 'solidarity',
        eligibility_rules: String(s.assistance?.governance?.eligibility_rules || ''),
        contribution_periodicity: s.assistance?.governance?.contribution_periodicity || 'monthly',
        covered_event_categories: Array.isArray(s.assistance?.governance?.covered_event_categories)
          ? s.assistance.governance.covered_event_categories.map((x) => String(x || '').trim()).filter(Boolean)
          : [],
        category_thresholds: Array.isArray(s.assistance?.governance?.category_thresholds)
          ? s.assistance.governance.category_thresholds
              .map((row) => ({
                category: String(row?.category || '').trim().toLowerCase(),
                min_amount: Math.max(0, Number(row?.min_amount) || 0),
                max_amount: Math.max(0, Number(row?.max_amount) || 0),
              }))
              .filter((row) => row.category)
          : [],
        assistance_min_amount: Math.max(0, Number(s.assistance?.governance?.assistance_min_amount) || 0),
        assistance_max_amount: Math.max(0, Number(s.assistance?.governance?.assistance_max_amount) || 0),
      },
      member_lifecycle: {
        membership_mode: s.assistance?.member_lifecycle?.membership_mode || 'open',
        active_statuses: Array.isArray(s.assistance?.member_lifecycle?.active_statuses)
          ? s.assistance.member_lifecycle.active_statuses.map((x) => String(x || '').trim()).filter(Boolean)
          : ['active', 'suspended'],
        minimum_seniority_months: Math.max(
          0,
          Number(s.assistance?.member_lifecycle?.minimum_seniority_months) || 0
        ),
        contribution_situation_mode:
          s.assistance?.member_lifecycle?.contribution_situation_mode || 'auto',
        track_benefit_history: Boolean(s.assistance?.member_lifecycle?.track_benefit_history),
      },
      financial_mechanics: (() => {
        const fm = s.assistance?.financial_mechanics || {};
        const types = Array.isArray(fm.exceptional_contribution_types)
          ? fm.exceptional_contribution_types
              .map((row) => ({
                key: normalizeExceptionalTypeKey(row?.key || row?.id),
                label: String(row?.label || '').trim(),
              }))
              .filter((row) => row.key && row.label)
          : [];
        const firstLabel = types[0]?.label;
        return {
          expected_contribution_amount: Math.max(0, Number(fm.expected_contribution_amount) || 0),
          exceptional_contribution_enabled: Boolean(fm.exceptional_contribution_enabled),
          exceptional_contribution_label:
            (firstLabel ||
              String(fm.exceptional_contribution_label || '').trim() ||
              'Contribution exceptionnelle') || 'Contribution exceptionnelle',
          exceptional_contribution_types: types,
          allow_arrears_tracking: Boolean(fm.allow_arrears_tracking),
        };
      })(),
      case_workflow: {
        require_reason: s.assistance?.case_workflow?.require_reason !== false,
        require_decision_note: s.assistance?.case_workflow?.require_decision_note !== false,
        close_after_days: Math.max(0, Number(s.assistance?.case_workflow?.close_after_days) || 0),
        notifications: (() => {
          const d = DEFAULT_ASSISTANCE_SETTINGS.case_workflow.notifications;
          const n = s.assistance?.case_workflow?.notifications || {};
          const raw = String(n.staff_delivery_mode || d.staff_delivery_mode).toLowerCase();
          const mode = raw === 'by_roles' ? 'by_roles' : 'all_non_members';
          const staff_roles = Array.isArray(n.staff_roles)
            ? n.staff_roles
                .map((x) => String(x || '').trim().toLowerCase())
                .filter(Boolean)
            : [...(d.staff_roles || [])];
          return {
            staff_delivery_mode: mode,
            staff_roles: mode === 'by_roles' ? staff_roles : [],
            on_case_created_member: n.on_case_created_member !== false,
            on_case_created_staff: n.on_case_created_staff !== false,
            on_case_submitted_member: n.on_case_submitted_member !== false,
            on_case_submitted_staff: n.on_case_submitted_staff !== false,
            on_case_approved_member: n.on_case_approved_member !== false,
            on_case_rejected_member: n.on_case_rejected_member !== false,
            on_case_paid_member: n.on_case_paid_member !== false,
          };
        })(),
      },
      transparency: {
        reports_frequency: s.assistance?.transparency?.reports_frequency || 'monthly',
        enable_aid_summary: s.assistance?.transparency?.enable_aid_summary !== false,
        enable_contributions_summary:
          s.assistance?.transparency?.enable_contributions_summary !== false,
        enable_case_tracking: s.assistance?.transparency?.enable_case_tracking !== false,
      },
    },
    financial: {
      contribution_amount: s.financial?.contribution_amount,
      contribution_frequency: s.financial?.contribution_frequency,
      fiscal_year_start: s.financial?.fiscal_year_start,
      fiscal_year_end: s.financial?.fiscal_year_end,
      currency: s.financial?.currency,
      contribution_billing: (() => {
        const b = s.financial?.contribution_billing || {};
        return {
          auto_create_on_month_start: b.auto_create_on_month_start !== false,
          notify_members_on_new_period: b.notify_members_on_new_period !== false,
          payment_deadline_day: Math.min(28, Math.max(1, Number(b.payment_deadline_day) || 15)),
          arrears_warning_min_months: Math.min(12, Math.max(1, Number(b.arrears_warning_min_months) || 3)),
          arrears_warning_max_months: Math.min(12, Math.max(1, Number(b.arrears_warning_max_months) || 4)),
          arrears_notice_period_days: Math.max(1, Number(b.arrears_notice_period_days) || 15),
          arrears_deactivation_notice_days: Math.max(1, Number(b.arrears_deactivation_notice_days) || 15),
        };
      })(),
      contribution_notifications: (() => {
        const n = s.financial?.contribution_notifications || {};
        return {
          notify_member_pending: n.notify_member_pending !== false,
          notify_member_approved: n.notify_member_approved !== false,
          notify_member_rejected: n.notify_member_rejected !== false,
        };
      })(),
    },
  };
  if (s.project_portal && typeof s.project_portal === 'object') {
    const pp = s.project_portal;
    out.project_portal = {
      site_description: String(pp.site_description ?? '').trim(),
      support_email: String(pp.support_email ?? '').trim(),
      public_domain: String(pp.public_domain ?? '').trim(),
      login_hero_image: String(pp.login_hero_image ?? '').trim(),
      login_hero_title: String(pp.login_hero_title ?? '').trim(),
      login_hero_subtitle: String(pp.login_hero_subtitle ?? '').trim(),
      login_hero_kicker: String(pp.login_hero_kicker ?? '').trim(),
      login_feature_1_title: String(pp.login_feature_1_title ?? '').trim(),
      login_feature_1_desc: String(pp.login_feature_1_desc ?? '').trim(),
      login_feature_2_title: String(pp.login_feature_2_title ?? '').trim(),
      login_feature_2_desc: String(pp.login_feature_2_desc ?? '').trim(),
    };
  }
  return out;
}

const PAYMENT_KIND_LABELS = {
  especes: 'Espèces',
  virement: 'Virement',
  carte_bancaire: 'Carte bancaire',
  mobile_money: 'Mobile money',
  cheque: 'Chèque',
  prelevement: 'Prélèvement',
  autre: 'Autre',
};

const SETTINGS_TAB_IDS = new Set([
  'organization',
  'financial',
  'assistance',
  'events',
  'system',
]);

const DEFAULT_ASSISTANCE_SETTINGS = {
  default_case_amount: 0,
  governance: {
    fund_type: 'solidarity',
    eligibility_rules: '',
    contribution_periodicity: 'monthly',
    covered_event_categories: ['deces', 'hospitalisation'],
    category_thresholds: [
      { category: 'deces', min_amount: 0, max_amount: 0 },
      { category: 'hospitalisation', min_amount: 0, max_amount: 0 },
    ],
    assistance_min_amount: 0,
    assistance_max_amount: 0,
  },
  member_lifecycle: {
    membership_mode: 'open',
    active_statuses: ['active', 'suspended'],
    minimum_seniority_months: 0,
    contribution_situation_mode: 'auto',
    track_benefit_history: true,
  },
  financial_mechanics: {
    expected_contribution_amount: 0,
    exceptional_contribution_enabled: false,
    exceptional_contribution_label: 'Contribution exceptionnelle',
    exceptional_contribution_types: [],
    allow_arrears_tracking: true,
  },
  case_workflow: {
    require_reason: true,
    require_decision_note: true,
    close_after_days: 0,
    notifications: {
      /** Tous les comptes non-membre, ou rôles listés (IAM / Entrea) */
      staff_delivery_mode: 'all_non_members',
      staff_roles: [
        'org_admin',
        'org_manager',
        'manager',
        'president',
        'tresorier',
        'validateur',
        'secretaire',
      ],
      on_case_created_member: true,
      on_case_created_staff: true,
      on_case_submitted_member: true,
      on_case_submitted_staff: true,
      on_case_approved_member: true,
      on_case_rejected_member: true,
      on_case_paid_member: true,
    },
  },
  transparency: {
    reports_frequency: 'monthly',
    enable_aid_summary: true,
    enable_contributions_summary: true,
    enable_case_tracking: true,
  },
};

const ASSISTANCE_SUB_TABS = [
  { id: 'governance', label: 'Gouvernance' },
  { id: 'members', label: 'Vie des membres' },
  { id: 'finance', label: 'Mécanique financière' },
  { id: 'cases', label: 'Allocations & événements' },
  { id: 'transparency', label: 'Transparence' },
];

/** Rôles IAM utilisés pour filtrer les notifications « équipe » (mode restreint). */
const NOTIFICATION_STAFF_ROLE_OPTIONS = [
  { value: 'org_admin', label: 'Admin organisation' },
  { value: 'org_manager', label: 'Gestionnaire organisation' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
  { value: 'president', label: 'Président' },
  { value: 'tresorier', label: 'Trésorier' },
  { value: 'validateur', label: 'Validateur' },
  { value: 'secretaire', label: 'Secrétaire' },
  { value: 'secretary', label: 'Secretary' },
];

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { error, success } = useToast();
  const { confirm } = useConfirmDialog();
  const { user } = useAuth();
  const { settings: contextSettings, refreshSettings } = useSettings();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('organization');
  const [assistanceSubTab, setAssistanceSubTab] = useState('governance');
  const [showBankModal, setShowBankModal] = useState(false);
  const [showSignatoryModal, setShowSignatoryModal] = useState(false);
  const [editingSignatoryIndex, setEditingSignatoryIndex] = useState(null);
  const [newEventType, setNewEventType] = useState('');
  const [editingEventTypeIndex, setEditingEventTypeIndex] = useState(null);
  const [newMeetingMode, setNewMeetingMode] = useState('');
  const [editingMeetingModeIndex, setEditingMeetingModeIndex] = useState(null);
  const [newAssistanceCategory, setNewAssistanceCategory] = useState('');
  const [editingAssistanceCategoryIndex, setEditingAssistanceCategoryIndex] = useState(null);
  
  // PIN Management
  const [oldPin, setOldPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [savingPin, setSavingPin] = useState(false);
  const [userHasPin, setUserHasPin] = useState(false);
  
  const [newBankAccount, setNewBankAccount] = useState({
    ledger_account_id: '',
    bank_name: '',
    account_holder: '',
    account_number: '',
    iban: '',
    swift_code: '',
    branch: '',
    currency: 'RWF',
    is_primary: false
  });

  const [newSignatory, setNewSignatory] = useState({
    member_id: '',
    name: '',
    role: '',
    position: '',
    phone: '',
    email: '',
    signature_image_url: '',
    is_active: true
  });
  const [members, setMembers] = useState([]);

  /** Ledgera : comptes, RIB, moyens de paiement référencés. */
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [ledgerBankDetails, setLedgerBankDetails] = useState([]);
  const [ledgerPaymentMethods, setLedgerPaymentMethods] = useState([]);
  const [ledgerMeta, setLedgerMeta] = useState(null);
  const [ledgerBalance, setLedgerBalance] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [showLedgerAccountModal, setShowLedgerAccountModal] = useState(false);
  const [newLedgerAccount, setNewLedgerAccount] = useState({
    name: '',
    currency: 'RWF',
    account_type: 'banque',
  });
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [financialSections, setFinancialSections] = useState({
    banking: true,
    signatories: true,
    contributions: true,
  });
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    name: '',
    kind: 'virement',
    ledger_account_id: '',
  });

  const setSettingsRouteTab = (tabId, nextSubTab = null) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', tabId);
    if (tabId === 'assistance') {
      const sub = nextSubTab || assistanceSubTab || 'governance';
      params.set('sub', sub);
    } else {
      params.delete('sub');
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const loadLedgerSnapshot = async () => {
    try {
      setLedgerLoading(true);
      const [metaRes, balRes, accRes, bdRes, pmRes] = await Promise.all([
        ledgerPlatformAPI.getMeta(),
        ledgerPlatformAPI.getOrgBalance(),
        ledgerPlatformAPI.listAccounts({ include_inactive: false }),
        ledgerPlatformAPI.listBankDetails({ limit: 200, skip: 0 }),
        ledgerPlatformAPI.listPaymentMethods({ include_inactive: true, limit: 200, skip: 0 }),
      ]);
      setLedgerMeta(metaRes.data || {});
      setLedgerBalance(balRes.data || null);
      const accRaw = accRes.data;
      setLedgerAccounts(Array.isArray(accRaw?.accounts) ? accRaw.accounts : []);
      const bdRaw = bdRes.data;
      setLedgerBankDetails(Array.isArray(bdRaw?.bank_details) ? bdRaw.bank_details : []);
      const pmRaw = pmRes.data;
      setLedgerPaymentMethods(Array.isArray(pmRaw?.payment_methods) ? pmRaw.payment_methods : []);
    } catch (err) {
      console.error('Ledger (paramètres):', err);
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    checkUserPIN();
    loadMembers();
    loadLedgerSnapshot();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const sub = params.get('sub');
    if (tab === 'banking' || tab === 'signatories') {
      setActiveTab('financial');
      return;
    }
    if (tab && SETTINGS_TAB_IDS.has(tab)) {
      setActiveTab(tab);
    }
    if (tab === 'assistance') {
      const allowedSubTabs = new Set(ASSISTANCE_SUB_TABS.map((x) => x.id));
      if (sub && allowedSubTabs.has(sub)) {
        setAssistanceSubTab(sub);
      } else {
        setAssistanceSubTab('governance');
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (activeTab !== 'assistance') return;
    const params = new URLSearchParams(location.search);
    const currentSub = params.get('sub');
    if (currentSub === assistanceSubTab) return;
    setSettingsRouteTab('assistance', assistanceSubTab);
  }, [activeTab, assistanceSubTab, location.search]);

  const loadMembers = async () => {
    try {
      const response = await peoplePlatformAPI.list({ limit: 2000, skip: 0 });
      setMembers(mapPeopleListResponse(response.data));
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  };


  const checkUserPIN = async () => {
    try {
      const response = await authAPI.getMe();
      // On suppose que si l'utilisateur a un PIN, il sera présent dans la réponse
      // Pour l'instant, on ne peut pas vérifier directement, donc on laisse l'utilisateur essayer de modifier
      setUserHasPin(false); // Par défaut, on assume qu'il n'y a pas de PIN (sera vérifié lors de la tentative de modification)
    } catch (err) {
      console.error('Erreur lors de la vérification du PIN:', err);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAll();
      const data = response.data || {};
      setSettings({
        ...data,
        bank_accounts: [],
        signatories: data.signatories || [],
        organization: data.organization || {
          name: '',
          full_name: '',
          registration_number: '',
          tax_id: '',
          address: '',
          phone: '',
          email: '',
          website: '',
          logo_url: '',
          description: '',
          support_email: '',
          portal_meta_description: '',
        },
        project_portal: (() => {
          const d = {
            name: '',
            site_description: '',
            support_email: '',
            public_domain: '',
            login_hero_image: '',
            login_hero_title: '',
            login_hero_subtitle: '',
            login_hero_kicker: '',
            login_feature_1_title: '',
            login_feature_1_desc: '',
            login_feature_2_title: '',
            login_feature_2_desc: '',
          };
          return typeof data.project_portal === 'object' && data.project_portal
            ? { ...d, ...data.project_portal }
            : d;
        })(),
        financial: (() => {
          const f = data.financial || {
            contribution_amount: 5000,
            contribution_frequency: 'monthly',
            fiscal_year_start: '',
            fiscal_year_end: '',
            currency: 'RWF',
          };
          return {
            ...f,
            contribution_notifications: {
              ...DEFAULT_FINANCIAL_NOTIFICATIONS,
              ...(f.contribution_notifications || {}),
            },
          };
        })(),
        system: (() => {
          const sys = data.system || {
            timezone: 'Africa/Kigali',
            date_format: 'DD/MM/YYYY',
            language: 'fr',
            email_notifications: true,
            sms_notifications: false,
            maintenance_mode: false,
          };
          return {
            ...sys,
            ui_surfaces: normalizeUiSurfaces(sys.ui_surfaces),
          };
        })(),
        events: data.events || {
          event_types: ['meeting', 'deadline', 'event'],
          meeting_modes: ['présentiel', 'en ligne', 'hybride'],
          default_event_type: 'meeting',
          default_meeting_mode: 'présentiel'
        },
        assistance: {
          ...DEFAULT_ASSISTANCE_SETTINGS,
          ...(data.assistance || {}),
          governance: {
            ...DEFAULT_ASSISTANCE_SETTINGS.governance,
            ...(data.assistance?.governance || {}),
          },
          member_lifecycle: {
            ...DEFAULT_ASSISTANCE_SETTINGS.member_lifecycle,
            ...(data.assistance?.member_lifecycle || {}),
          },
          financial_mechanics: (() => {
            const base = {
              ...DEFAULT_ASSISTANCE_SETTINGS.financial_mechanics,
              ...(data.assistance?.financial_mechanics || {}),
            };
            let types = Array.isArray(base.exceptional_contribution_types)
              ? base.exceptional_contribution_types
              : [];
            types = types
              .map((row) => ({
                key: normalizeExceptionalTypeKey(row?.key || row?.id),
                label: String(row?.label || '').trim(),
              }))
              .filter((row) => row.key && row.label);
            if (types.length === 0) {
              const leg =
                String(base.exceptional_contribution_label || 'Contribution exceptionnelle').trim() ||
                'Contribution exceptionnelle';
              types = [{ key: 'adhesion', label: leg }];
            }
            return {
              ...base,
              exceptional_contribution_types: types,
              exceptional_contribution_label: types[0]?.label || base.exceptional_contribution_label,
            };
          })(),
          case_workflow: {
            ...DEFAULT_ASSISTANCE_SETTINGS.case_workflow,
            ...(data.assistance?.case_workflow || {}),
            notifications: {
              ...DEFAULT_ASSISTANCE_SETTINGS.case_workflow.notifications,
              ...(data.assistance?.case_workflow?.notifications || {}),
            },
          },
          transparency: {
            ...DEFAULT_ASSISTANCE_SETTINGS.transparency,
            ...(data.assistance?.transparency || {}),
          },
        },
      });
    } catch (err) {
      error('Erreur lors du chargement des paramètres');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const fm = settings.assistance?.financial_mechanics;
      if (fm?.exceptional_contribution_enabled) {
        const types = Array.isArray(fm.exceptional_contribution_types) ? fm.exceptional_contribution_types : [];
        const keys = types.map((t) => normalizeExceptionalTypeKey(t?.key || t?.id));
        if (types.some((t) => !String(t?.label || '').trim())) {
          error('Renseignez un libellé pour chaque type de contribution exceptionnelle.');
          return;
        }
        if (new Set(keys).size !== keys.length) {
          error('Les clés des types exceptionnels doivent être uniques.');
          return;
        }
      }
      setSaving(true);
      await settingsAPI.update(buildEntreaSettingsPayload(settings));
      success('Paramètres sauvegardés avec succès');
      refreshSettings();
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateLedgerBankAccount = async () => {
    if (!newLedgerAccount.name.trim()) {
      error('Nom du compte comptable requis');
      return;
    }
    try {
      await ledgerPlatformAPI.createAccount({
        name: newLedgerAccount.name.trim(),
        account_type: newLedgerAccount.account_type || 'banque',
        currency: newLedgerAccount.currency || 'RWF',
        opening_balance: 0,
      });
      success('Compte Ledgera créé');
      setShowLedgerAccountModal(false);
      setNewLedgerAccount({ name: '', currency: 'RWF', account_type: 'banque' });
      await loadLedgerSnapshot();
    } catch (err) {
      error(err.response?.data?.detail || err.message || 'Erreur création compte');
    }
  };

  const handleAddBankAccount = async () => {
    if (!newBankAccount.ledger_account_id) {
      error('Sélectionnez un compte comptable banque (Ledgera)');
      return;
    }
    if (!newBankAccount.bank_name.trim()) {
      error('Nom de la banque requis');
      return;
    }
    try {
      await ledgerPlatformAPI.createBankDetails({
        ledger_account_id: newBankAccount.ledger_account_id,
        bank_name: newBankAccount.bank_name.trim(),
        iban: newBankAccount.iban?.trim() || undefined,
        bic_swift: newBankAccount.swift_code?.trim() || undefined,
        account_number: newBankAccount.account_number?.trim() || undefined,
        branch_name: newBankAccount.branch?.trim() || undefined,
        metadata: newBankAccount.account_holder
          ? { account_holder_label: newBankAccount.account_holder }
          : undefined,
      });
      success('Coordonnées bancaires enregistrées dans Ledgera');
      setShowBankModal(false);
      resetBankForm();
      await loadLedgerSnapshot();
    } catch (err) {
      error(err.response?.data?.detail || err.message || 'Erreur');
    }
  };

  const handleAddSignatory = async () => {
    if (!newSignatory.member_id) {
      error('Veuillez sélectionner un membre');
      return;
    }
    if (!newSignatory.name || !newSignatory.role) {
      error('Le nom et le rôle sont obligatoires');
      return;
    }
    try {
      const signatory = { ...newSignatory, id: Date.now().toString() };
      await settingsAPI.addSignatory(signatory);
      success('Signataire ajouté');
      setShowSignatoryModal(false);
      resetSignatoryForm();
      loadSettings();
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteBankAccount = async (id) => {
    const confirmed = await confirm({
      title: 'Supprimer les coordonnées bancaires',
      message: 'Supprimer cette fiche RIB dans Ledgera ?',
      type: 'danger'
    });
    if (!confirmed) return;
    try {
      await ledgerPlatformAPI.deleteBankDetails(id);
      success('Coordonnées supprimées');
      await loadLedgerSnapshot();
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleAddLedgerPaymentMethod = async () => {
    if (!newPaymentMethod.ledger_account_id || !newPaymentMethod.name.trim()) {
      error('Compte Ledgera et nom du moyen de paiement requis');
      return;
    }
    try {
      await ledgerPlatformAPI.createPaymentMethod({
        name: newPaymentMethod.name.trim(),
        kind: newPaymentMethod.kind,
        ledger_account_id: newPaymentMethod.ledger_account_id,
        is_default: ledgerPaymentMethods.length === 0,
      });
      success('Moyen de paiement ajouté (Ledgera)');
      setShowPaymentMethodModal(false);
      setNewPaymentMethod({ name: '', kind: 'virement', ledger_account_id: '' });
      await loadLedgerSnapshot();
    } catch (err) {
      error(err.response?.data?.detail || err.message || 'Erreur');
    }
  };

  const handleSetDefaultPaymentMethod = async (id) => {
    try {
      await ledgerPlatformAPI.patchPaymentMethod(id, { is_default: true });
      await loadLedgerSnapshot();
      success('Moyen de paiement par défaut mis à jour');
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeactivatePaymentMethod = async (id) => {
    const confirmed = await confirm({
      title: 'Désactiver le moyen de paiement',
      message: 'Le moyen sera désactivé dans Ledgera (réversible côté admin).',
      type: 'info',
    });
    if (!confirmed) return;
    try {
      await ledgerPlatformAPI.deactivatePaymentMethod(id);
      await loadLedgerSnapshot();
      success('Moyen désactivé');
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteSignatory = async (id) => {
    const confirmed = await confirm({
      title: 'Supprimer le signataire',
      message: 'Êtes-vous sûr de vouloir supprimer ce signataire ?',
      type: 'danger'
    });
    if (!confirmed) return;
    try {
      await settingsAPI.deleteSignatory(id);
      success('Signataire supprimé');
      loadSettings();
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur');
    }
  };

  const resetBankForm = () => {
    setNewBankAccount({
      ledger_account_id: '',
      bank_name: '',
      account_holder: '',
      account_number: '',
      iban: '',
      swift_code: '',
      branch: '',
      currency: 'RWF',
      is_primary: false
    });
  };

  const banqueLedgerAccounts = ledgerAccounts.filter(
    (a) => (a.account_type || '').toLowerCase() === 'banque' && a.is_active !== false
  );

  const ledgerAccountName = (id) => {
    if (!id) return '—';
    const a = ledgerAccounts.find((x) => String(x.id) === String(id));
    return a?.name || String(id).slice(0, 8);
  };

  const paymentKindOptions =
    Array.isArray(ledgerMeta?.payment_method_kinds) && ledgerMeta.payment_method_kinds.length > 0
      ? ledgerMeta.payment_method_kinds
      : Object.keys(PAYMENT_KIND_LABELS);

  const resetSignatoryForm = () => {
    setNewSignatory({
      member_id: '',
      name: '',
      role: '',
      position: '',
      phone: '',
      email: '',
      signature_image_url: '',
      is_active: true
    });
    setEditingSignatoryIndex(null);
  };

  // Gestion des types d'événements
  const handleAddEventType = () => {
    if (!newEventType.trim()) {
      error('Veuillez entrer un type d\'événement');
      return;
    }
    const eventTypes = Array.isArray(settings.events?.event_types) ? [...settings.events.event_types] : [];
    if (eventTypes.includes(newEventType.trim())) {
      error('Ce type d\'événement existe déjà');
      return;
    }
    eventTypes.push(newEventType.trim());
    setSettings({
      ...settings,
      events: { ...(settings.events || {}), event_types: eventTypes }
    });
    setNewEventType('');
  };

  const handleEditEventType = (index, type) => {
    setNewEventType(type);
    setEditingEventTypeIndex(index);
  };

  const handleUpdateEventType = () => {
    if (!newEventType.trim()) {
      error('Veuillez entrer un type d\'événement');
      return;
    }
    const eventTypes = Array.isArray(settings.events?.event_types) ? [...settings.events.event_types] : [];
    if (eventTypes[editingEventTypeIndex] !== newEventType.trim() && eventTypes.includes(newEventType.trim())) {
      error('Ce type d\'événement existe déjà');
      return;
    }
    eventTypes[editingEventTypeIndex] = newEventType.trim();
    setSettings({
      ...settings,
      events: { ...(settings.events || {}), event_types: eventTypes }
    });
    setNewEventType('');
    setEditingEventTypeIndex(null);
  };

  const handleDeleteEventType = async (index) => {
    const eventType = settings.events?.event_types?.[index];
    const confirmed = await confirm({
      title: 'Supprimer le type d\'événement',
      message: `Êtes-vous sûr de vouloir supprimer le type "${eventType}" ?`,
      type: 'danger'
    });
    if (!confirmed) return;
    const eventTypes = Array.isArray(settings.events?.event_types) ? [...settings.events.event_types] : [];
    eventTypes.splice(index, 1);
    setSettings({
      ...settings,
      events: { ...(settings.events || {}), event_types: eventTypes }
    });
  };

  // Gestion des modes de réunion
  const handleAddMeetingMode = () => {
    if (!newMeetingMode.trim()) {
      error('Veuillez entrer un mode de réunion');
      return;
    }
    const meetingModes = Array.isArray(settings.events?.meeting_modes) ? [...settings.events.meeting_modes] : [];
    if (meetingModes.includes(newMeetingMode.trim())) {
      error('Ce mode de réunion existe déjà');
      return;
    }
    meetingModes.push(newMeetingMode.trim());
    setSettings({
      ...settings,
      events: { ...(settings.events || {}), meeting_modes: meetingModes }
    });
    setNewMeetingMode('');
  };

  const handleEditMeetingMode = (index, mode) => {
    setNewMeetingMode(mode);
    setEditingMeetingModeIndex(index);
  };

  const handleUpdateMeetingMode = () => {
    if (!newMeetingMode.trim()) {
      error('Veuillez entrer un mode de réunion');
      return;
    }
    const meetingModes = Array.isArray(settings.events?.meeting_modes) ? [...settings.events.meeting_modes] : [];
    if (meetingModes[editingMeetingModeIndex] !== newMeetingMode.trim() && meetingModes.includes(newMeetingMode.trim())) {
      error('Ce mode de réunion existe déjà');
      return;
    }
    meetingModes[editingMeetingModeIndex] = newMeetingMode.trim();
    setSettings({
      ...settings,
      events: { ...(settings.events || {}), meeting_modes: meetingModes }
    });
    setNewMeetingMode('');
    setEditingMeetingModeIndex(null);
  };

  const handleDeleteMeetingMode = async (index) => {
    const meetingMode = settings.events?.meeting_modes?.[index];
    const confirmed = await confirm({
      title: 'Supprimer le mode de réunion',
      message: `Êtes-vous sûr de vouloir supprimer le mode "${meetingMode}" ?`,
      type: 'danger'
    });
    if (!confirmed) return;
    const meetingModes = Array.isArray(settings.events?.meeting_modes) ? [...settings.events.meeting_modes] : [];
    meetingModes.splice(index, 1);
    setSettings({
      ...settings,
      events: { ...(settings.events || {}), meeting_modes: meetingModes }
    });
  };

  const getAssistanceCategoryThresholds = () => {
    const raw = settings?.assistance?.governance?.category_thresholds;
    const normalizedRows = Array.isArray(raw)
      ? raw
      .map((row) => ({
        category: String(row?.category || '').trim().toLowerCase(),
        min_amount: Math.max(0, Number(row?.min_amount) || 0),
        max_amount: Math.max(0, Number(row?.max_amount) || 0),
      }))
      .filter((row) => row.category)
      : [];
    if (normalizedRows.length > 0) return normalizedRows;
    const legacy = Array.isArray(settings?.assistance?.governance?.covered_event_categories)
      ? settings.assistance.governance.covered_event_categories
      : [];
    return legacy
      .map((category) => String(category || '').trim().toLowerCase())
      .filter(Boolean)
      .map((category) => ({
        category,
        min_amount: 0,
        max_amount: 0,
      }));
  };

  const handleAddAssistanceCategory = () => {
    const val = String(newAssistanceCategory || '').trim().toLowerCase();
    if (!val) {
      error('Veuillez entrer une catégorie');
      return;
    }
    const categoryThresholds = [...getAssistanceCategoryThresholds()];
    if (categoryThresholds.some((row) => row.category === val)) {
      error('Cette catégorie existe déjà');
      return;
    }
    categoryThresholds.push({
      category: val,
      min_amount: 0,
      max_amount: 0,
    });
    setSettings({
      ...settings,
      assistance: {
        ...(settings.assistance || {}),
        governance: {
          ...(settings.assistance?.governance || {}),
          covered_event_categories: categoryThresholds.map((row) => row.category),
          category_thresholds: categoryThresholds,
        },
      },
    });
    setNewAssistanceCategory('');
  };

  const handleEditAssistanceCategory = (index, category) => {
    setNewAssistanceCategory(String(category || ''));
    setEditingAssistanceCategoryIndex(index);
  };

  const handleUpdateAssistanceCategory = () => {
    const val = String(newAssistanceCategory || '').trim().toLowerCase();
    if (!val) {
      error('Veuillez entrer une catégorie');
      return;
    }
    const categoryThresholds = [...getAssistanceCategoryThresholds()];
    if (
      categoryThresholds[editingAssistanceCategoryIndex]?.category !== val &&
      categoryThresholds.some((row) => row.category === val)
    ) {
      error('Cette catégorie existe déjà');
      return;
    }
    if (editingAssistanceCategoryIndex === null) return;
    categoryThresholds[editingAssistanceCategoryIndex] = {
      ...(categoryThresholds[editingAssistanceCategoryIndex] || { min_amount: 0, max_amount: 0 }),
      category: val,
    };
    setSettings({
      ...settings,
      assistance: {
        ...(settings.assistance || {}),
        governance: {
          ...(settings.assistance?.governance || {}),
          covered_event_categories: categoryThresholds.map((row) => row.category),
          category_thresholds: categoryThresholds,
        },
      },
    });
    setNewAssistanceCategory('');
    setEditingAssistanceCategoryIndex(null);
  };

  const handleDeleteAssistanceCategory = async (index) => {
    const category = getAssistanceCategoryThresholds()[index]?.category;
    const confirmed = await confirm({
      title: 'Supprimer la catégorie',
      message: `Supprimer la catégorie "${category}" ?`,
      type: 'danger'
    });
    if (!confirmed) return;
    const categoryThresholds = [...getAssistanceCategoryThresholds()];
    categoryThresholds.splice(index, 1);
    if (editingAssistanceCategoryIndex === index) {
      setEditingAssistanceCategoryIndex(null);
      setNewAssistanceCategory('');
    }
    setSettings({
      ...settings,
      assistance: {
        ...(settings.assistance || {}),
        governance: {
          ...(settings.assistance?.governance || {}),
          covered_event_categories: categoryThresholds.map((row) => row.category),
          category_thresholds: categoryThresholds,
        },
      },
    });
  };

  const handleUpdateAssistanceCategoryThreshold = (index, field, rawValue) => {
    const categoryThresholds = [...getAssistanceCategoryThresholds()];
    if (!categoryThresholds[index]) return;
    const nextValue = Math.max(0, Number(rawValue) || 0);
    categoryThresholds[index] = {
      ...categoryThresholds[index],
      [field]: nextValue,
    };
    setSettings({
      ...settings,
      assistance: {
        ...(settings.assistance || {}),
        governance: {
          ...(settings.assistance?.governance || {}),
          covered_event_categories: categoryThresholds.map((row) => row.category),
          category_thresholds: categoryThresholds,
        },
      },
    });
  };

  const handleMemberSelect = (memberId) => {
    if (!memberId) {
      resetSignatoryForm();
      return;
    }
    const member = members.find(m => m.id === memberId);
    if (member) {
      setNewSignatory({
        member_id: memberId,
        name: member.name || '',
        role: member.role || '',
        position: '',
        phone: member.phone || '',
        email: member.email || '',
        signature_image_url: '',
        is_active: true
      });
    }
  };

  const handleSavePIN = async () => {
    // Nettoyer les PIN en enlevant tout ce qui n'est pas un chiffre
    const cleanNewPin = newPin.replace(/\D/g, '');
    const cleanConfirmPin = confirmPin.replace(/\D/g, '');
    const cleanOldPin = oldPin.replace(/\D/g, '');

    if (cleanNewPin.length !== 6 || cleanConfirmPin.length !== 6) {
      error('Le PIN doit contenir exactement 6 chiffres');
      return;
    }

    if (cleanNewPin !== cleanConfirmPin) {
      error('Les PIN ne correspondent pas');
      setConfirmPin('');
      return;
    }

    if (userHasPin && cleanOldPin.length !== 6) {
      error('L\'ancien PIN doit contenir 6 chiffres');
      return;
    }

    try {
      setSavingPin(true);
      // Si l'utilisateur a déjà un PIN, il doit fournir l'ancien
      await authAPI.createPin(cleanNewPin, userHasPin ? cleanOldPin : null);
      success('PIN créé/modifié avec succès');
      setOldPin('');
      setNewPin('');
      setConfirmPin('');
      setUserHasPin(true);
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la sauvegarde du PIN');
      if (err.response?.data?.detail?.includes('ancien')) {
        setOldPin('');
      }
    } finally {
      setSavingPin(false);
    }
  };

  const handlePINComplete = (pinValue, type) => {
    if (type === 'new') {
      setNewPin(pinValue);
    } else if (type === 'confirm') {
      setConfirmPin(pinValue);
      // Si les deux PIN correspondent et sont complets, on peut soumettre
      if (pinValue.length === 6 && newPin.length === 6 && pinValue === newPin && (!userHasPin || oldPin.length === 6)) {
        setTimeout(() => handleSavePIN(), 300);
      }
    } else if (type === 'old') {
      setOldPin(pinValue);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2f6fd4] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'organization', label: 'Fiche & affichage', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { id: 'financial', label: 'Paramètres financiers', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'assistance', label: 'Allocations', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
    { id: 'events', label: 'Événements', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { id: 'system', label: 'Système', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' }
  ];

  return (
    <div className="space-y-3 md:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 md:border-[#bcd4f6] p-3 md:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 md:gap-4">
          <div className="flex-1">
            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-[#0f3350] mb-0.5 md:mb-2">Paramètres du projet</h1>
            <p className="text-xs sm:text-sm md:text-base text-slate-600">
              Fiche, finances, événements et système pour votre projet Entrea.
            </p>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="px-3 py-1.5 md:px-4 md:py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium text-xs md:text-sm whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
          >
            {saving ? 'Sauvegarde...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Tabs - Scrollable on mobile */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 sticky top-[57px] md:top-0 z-30">
        <div className="border-b border-slate-200">
          <nav className="flex gap-0.5 md:gap-8 px-2 md:px-6 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSettingsRouteTab(tab.id, tab.id === 'assistance' ? assistanceSubTab : null);
                }}
                className={`flex items-center gap-1 md:gap-2 py-2 md:py-4 px-1.5 md:px-1 border-b-2 font-medium text-xs md:text-sm transition whitespace-nowrap flex-shrink-0 min-w-fit ${
                  activeTab === tab.id
                    ? 'border-[#2f6fd4] text-[#0f3350]'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <svg className="w-3.5 h-3.5 md:w-5 md:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                </svg>
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.replace('Paramètres ', '').split(' ')[0]}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 md:p-6">
        {activeTab === 'organization' && (
          <div className="space-y-4 md:space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
              <div>
                <h2 className="text-base md:text-xl font-bold text-[#0f3350]">Fiche affichée (projet)</h2>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                  La fiche Entrea et le portail (meta, support, domaine) peuvent être ajustés ici par l’admin
                  organisation ou projet. Le nom officiel du projet est défini dans la console admin ; les champs
                  « portail » ci-dessous mettent à jour le même enregistrement projet (équivalent aux variables
                  d’environnement de déploiement lorsqu’elles ne sont pas renseignées).
                </p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {ENABLE_EMBEDDED_SAAS_UI ? (
                  <button
                    type="button"
                    onClick={() => navigate('/organization')}
                    className="px-4 py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition text-sm"
                  >
                    Écran organisation (mode embarqué)
                  </button>
                ) : ADMIN_CONSOLE_URL ? (
                  <a
                    href={adminConsoleUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition text-sm inline-flex items-center gap-1"
                  >
                    Console Uruganda <span aria-hidden>↗</span>
                  </a>
                ) : null}
              </div>
            </div>
            {!ENABLE_EMBEDDED_SAAS_UI && !ADMIN_CONSOLE_URL && null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">Nom *</label>
                <input
                  type="text"
                  value={settings.organization?.name || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    organization: { ...(settings.organization || {}), name: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nom complet</label>
                <input
                  type="text"
                  value={settings.organization?.full_name || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    organization: { ...(settings.organization || {}), full_name: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={settings.organization?.phone || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    organization: { ...(settings.organization || {}), phone: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">E-mail de contact</label>
                <input
                  type="email"
                  value={settings.organization?.email || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    organization: { ...(settings.organization || {}), email: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                <p className="mt-1 text-xs text-slate-500">Coordonnées générales du projet.</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Logo de l&apos;organisation (URL)</label>
                <input
                  type="url"
                  value={settings.organization?.logo_url || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    organization: { ...(settings.organization || {}), logo_url: e.target.value },
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="https://…/logo.png"
                />
                <p className="mt-1 text-xs text-slate-500">Affiché sur l&apos;écran de connexion web lorsque l&apos;identifiant est reconnu.</p>
              </div>
              <div className="md:col-span-2 rounded-lg border border-[#bcd4f6] bg-[#f8fafc] p-4 space-y-4">
                <h3 className="text-sm font-semibold text-[#0f3350]">Portail (projet — console admin &amp; Entrea)</h3>
                <p className="text-xs text-slate-600">
                  Ces valeurs sont stockées sur le <strong>projet</strong> (console Uruganda → Projets). Prioritaires
                  sur la fiche ci-dessus et sur <code className="text-[11px]">REACT_APP_SITE_DESCRIPTION</code>,{' '}
                  <code className="text-[11px]">REACT_APP_SUPPORT_EMAIL</code> lorsqu’elles sont renseignées.
                </p>
                <div>
                  <label className="block text-sm font-medium mb-1">Nom projet (console)</label>
                  <input
                    type="text"
                    readOnly
                    value={settings.project_portal?.name || ''}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-slate-600"
                  />
                  <p className="mt-1 text-xs text-slate-500">Modifiable dans la console admin, onglet Projet.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description courte (SEO / partages)</label>
                  <textarea
                    value={settings.project_portal?.site_description || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      project_portal: { ...(settings.project_portal || {}), site_description: e.target.value },
                    })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                    placeholder="Ex. Cotisations et allocations CSA Dufashanye — Entrea sur Uruganda Platform."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">E-mail support membres</label>
                  <input
                    type="email"
                    value={settings.project_portal?.support_email || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      project_portal: { ...(settings.project_portal || {}), support_email: e.target.value },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                    placeholder="Ex. support@uruganda-platform.bi"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Domaine public (hôte)</label>
                  <input
                    type="text"
                    value={settings.project_portal?.public_domain || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      project_portal: { ...(settings.project_portal || {}), public_domain: e.target.value },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                    placeholder="ex. uruganda-platform.bi (sans https://)"
                  />
                </div>
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-sm font-semibold text-[#0f3350] mb-1">Écran de connexion web</h4>
                  <p className="text-xs text-slate-600 mb-3">
                    Personnalise le panneau gauche affiché quand un membre saisit son identifiant (aperçu organisation).
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Image de fond (URL)</label>
                      <input
                        type="url"
                        value={settings.project_portal?.login_hero_image || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          project_portal: { ...(settings.project_portal || {}), login_hero_image: e.target.value },
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        placeholder="https://…/photo.jpg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Surtitre</label>
                      <input
                        type="text"
                        value={settings.project_portal?.login_hero_kicker || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          project_portal: { ...(settings.project_portal || {}), login_hero_kicker: e.target.value },
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        placeholder="Ex. Espace membre"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Titre principal</label>
                      <input
                        type="text"
                        value={settings.project_portal?.login_hero_title || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          project_portal: { ...(settings.project_portal || {}), login_hero_title: e.target.value },
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        placeholder="Ex. CSA Dufashanye — un seul espace"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Sous-titre</label>
                      <textarea
                        value={settings.project_portal?.login_hero_subtitle || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          project_portal: { ...(settings.project_portal || {}), login_hero_subtitle: e.target.value },
                        })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        placeholder="Ex. Cotisations, membres, allocations…"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Point fort 1 — titre</label>
                        <input
                          type="text"
                          value={settings.project_portal?.login_feature_1_title || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            project_portal: { ...(settings.project_portal || {}), login_feature_1_title: e.target.value },
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Point fort 2 — titre</label>
                        <input
                          type="text"
                          value={settings.project_portal?.login_feature_2_title || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            project_portal: { ...(settings.project_portal || {}), login_feature_2_title: e.target.value },
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Point fort 1 — description</label>
                        <textarea
                          value={settings.project_portal?.login_feature_1_desc || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            project_portal: { ...(settings.project_portal || {}), login_feature_1_desc: e.target.value },
                          })}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Point fort 2 — description</label>
                        <textarea
                          value={settings.project_portal?.login_feature_2_desc || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            project_portal: { ...(settings.project_portal || {}), login_feature_2_desc: e.target.value },
                          })}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Présentation du projet (texte libre)</label>
                <textarea
                  value={settings.organization?.description || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    organization: { ...(settings.organization || {}), description: e.target.value }
                  })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Ex. Projet entrea-dufashanye : instance Entrea de la CSA Dufashanye. Ici : cotisations, allocations, caisse. Sur la même plateforme Uruganda, Agora couvre initiatives, activités et rapports."
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Adresse</label>
                <textarea
                  value={settings.organization?.address || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    organization: { ...(settings.organization || {}), address: e.target.value }
                  })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            {(ENABLE_EMBEDDED_SAAS_UI || ADMIN_CONSOLE_URL) && (
              <div className="mt-2 rounded-lg border border-[#bcd4f6] bg-[#edf4ff]/50 p-3">
                <p className="text-xs text-slate-600">
                  La gouvernance projet (IAM, modules, politiques) est gérée dans la console admin.
                </p>
                <div className="mt-2">
                  {ENABLE_EMBEDDED_SAAS_UI ? (
                    <button
                      type="button"
                      onClick={() => navigate('/organization')}
                      className="px-3 py-1.5 text-sm bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition"
                    >
                      Ouvrir espace organisation
                    </button>
                  ) : (
                    <a
                      href={adminConsoleUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex px-3 py-1.5 text-sm bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition"
                    >
                      Ouvrir la console admin
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="border-t border-slate-200 pt-6 mt-2 space-y-4">
              <div>
                <h3 className="text-base font-bold text-[#0f3350]">Affichage web / mobile</h3>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                  Choisissez quels boutons et actions apparaissent sur ordinateur et sur téléphone.
                  Par défaut, les imports CSV restent réservés au web.
                </p>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2.5">Page</th>
                      <th className="px-3 py-2.5">Élément</th>
                      <th className="px-3 py-2.5 text-center">Web</th>
                      <th className="px-3 py-2.5 text-center">Mobile</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {UI_SURFACE_CATALOG.map((item) => {
                      const surfaces = normalizeUiSurfaces(settings.system?.ui_surfaces);
                      const flags = surfaces[item.id] || item.default;
                      const setFlag = (side, value) => {
                        const next = normalizeUiSurfaces(settings.system?.ui_surfaces);
                        next[item.id] = {
                          ...(next[item.id] || item.default),
                          [side]: Boolean(value),
                        };
                        setSettings({
                          ...settings,
                          system: {
                            ...(settings.system || {}),
                            ui_surfaces: next,
                          },
                        });
                      };
                      return (
                        <tr key={item.id} className="bg-white">
                          <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{item.page}</td>
                          <td className="px-3 py-2.5 font-medium text-slate-900">{item.label}</td>
                          <td className="px-3 py-2.5 text-center">
                            <input
                              type="checkbox"
                              checked={Boolean(flags.web)}
                              onChange={(e) => setFlag('web', e.target.checked)}
                              className="h-4 w-4 rounded border-slate-300 text-[#2f6fd4] focus:ring-[#2f6fd4]"
                              aria-label={`${item.label} — web`}
                            />
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <input
                              type="checkbox"
                              checked={Boolean(flags.mobile)}
                              onChange={(e) => setFlag('mobile', e.target.checked)}
                              className="h-4 w-4 rounded border-slate-300 text-[#2f6fd4] focus:ring-[#2f6fd4]"
                              aria-label={`${item.label} — mobile`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'financial' && (
          <div className="space-y-4 md:space-y-6">
            <div className="pb-1 border-b border-gray-100">
              <h2 className="text-base md:text-xl font-bold text-[#0f3350]">Paramètres financiers</h2>
              <p className="text-sm text-slate-600 mt-1">
                Vue consolidée: cotisations, comptes bancaires Ledgera, moyens de paiement et signataires.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setFinancialSections({ banking: false, signatories: false, contributions: false })}
                  className="px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Tout réduire
                </button>
                <button
                  type="button"
                  onClick={() => setFinancialSections({ banking: true, signatories: true, contributions: true })}
                  className="px-3 py-1.5 text-xs border border-[#bcd4f6] text-[#113a5b] rounded-lg hover:bg-[#edf4ff]"
                >
                  Tout afficher
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">Comptes bancaires (Ledgera)</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Chaque fiche RIB est liée à un compte comptable de type « banque ». Créez d&apos;abord ce compte si besoin.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setFinancialSections((s) => ({ ...s, banking: !s.banking }))}
                    className="px-4 py-2.5 min-h-[44px] bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm whitespace-nowrap"
                  >
                    {financialSections.banking ? 'Réduire' : 'Afficher'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNewLedgerAccount({ name: '', currency: 'RWF', account_type: 'banque' });
                      setShowLedgerAccountModal(true);
                    }}
                    className="px-4 py-2.5 min-h-[44px] bg-white border border-[#bcd4f6] text-[#113a5b] rounded-lg hover:bg-[#edf4ff] transition text-sm whitespace-nowrap"
                  >
                    + Compte comptable banque
                  </button>
                  <button
                    onClick={() => {
                      resetBankForm();
                      setShowBankModal(true);
                    }}
                    className="px-4 py-2.5 min-h-[44px] bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition text-sm md:text-base whitespace-nowrap"
                  >
                    + Ajouter un RIB
                  </button>
                </div>
              </div>
              {ledgerLoading && (
                <p className="text-xs text-gray-500">Chargement Ledgera…</p>
              )}
            </div>
            {financialSections.banking && (
            <div className="space-y-3 md:space-y-4">
              {ledgerBankDetails.map((bd) => (
                <div key={bd.id} className="border border-gray-200 rounded-lg p-3 md:p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 md:gap-3 mb-2 flex-wrap">
                        <h3 className="font-semibold text-base md:text-lg text-gray-900">{bd.bank_name}</h3>
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                          {ledgerAccountName(bd.ledger_account_id)}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-4 text-xs md:text-sm">
                        {bd.metadata?.account_holder_label && (
                          <div>
                            <span className="text-gray-500">Titulaire:</span> {bd.metadata.account_holder_label}
                          </div>
                        )}
                        {bd.account_number && (
                          <div>
                            <span className="text-gray-500">Numéro:</span> {bd.account_number}
                          </div>
                        )}
                        {bd.iban && (
                          <div>
                            <span className="text-gray-500">IBAN:</span> {bd.iban}
                          </div>
                        )}
                        {bd.bic_swift && (
                          <div>
                            <span className="text-gray-500">BIC/SWIFT:</span> {bd.bic_swift}
                          </div>
                        )}
                        {bd.branch_name && (
                          <div>
                            <span className="text-gray-500">Agence:</span> {bd.branch_name}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 sm:flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleDeleteBankAccount(bd.id)}
                        className="px-3 py-2 min-h-[36px] bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs md:text-sm"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {!ledgerLoading && ledgerBankDetails.length === 0 && (
                <p className="text-center text-gray-500 py-4">
                  Aucune coordonnée bancaire dans Ledgera. Ajoutez un compte « banque » puis un RIB.
                </p>
              )}
            </div>
            )}
          </div>
        )}

        {activeTab === 'financial' && (
          <div className="space-y-4 md:space-y-6 rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h3 className="text-base md:text-lg font-semibold text-gray-900">Signataires (projet)</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setFinancialSections((s) => ({ ...s, signatories: !s.signatories }))}
                  className="px-4 py-2.5 min-h-[44px] bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm whitespace-nowrap"
                >
                  {financialSections.signatories ? 'Réduire' : 'Afficher'}
                </button>
                <button
                  onClick={() => {
                    setEditingSignatoryIndex(null);
                    resetSignatoryForm();
                    setShowSignatoryModal(true);
                  }}
                  className="px-4 py-2.5 min-h-[44px] bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition text-sm md:text-base whitespace-nowrap"
                >
                  + Ajouter un signataire
                </button>
              </div>
            </div>
            {financialSections.signatories && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {(settings.signatories || []).map((signatory, index) => {
                const member = members.find(m => m.id === signatory.member_id);
                return (
                  <div key={signatory.id || index} className="border border-gray-200 rounded-lg p-3 md:p-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base md:text-lg text-gray-900">{signatory.name}</h3>
                        <p className="text-xs md:text-sm text-gray-600">{signatory.role}</p>
                        {signatory.position && <p className="text-xs text-gray-500">{signatory.position}</p>}
                        {member && (
                          <p className="text-xs text-gray-400 mt-1">
                            {member.phone && `📞 ${member.phone}`} {member.email && `✉️ ${member.email}`}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteSignatory(signatory.id)}
                        className="px-3 py-2 min-h-[36px] bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs md:text-sm sm:flex-shrink-0"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                );
              })}
              {(!settings.signatories || settings.signatories.length === 0) && (
                <p className="text-center text-gray-500 py-4 col-span-2">Aucun signataire enregistré</p>
              )}
            </div>
            )}
          </div>
        )}

        {activeTab === 'financial' && (
          <div className="space-y-4 md:space-y-6 rounded-lg border border-gray-200 p-4">
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h3 className="text-base md:text-lg font-semibold text-gray-900">Cotisations & exercice</h3>
                <button
                  type="button"
                  onClick={() => setFinancialSections((s) => ({ ...s, contributions: !s.contributions }))}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm whitespace-nowrap"
                >
                  {financialSections.contributions ? 'Réduire' : 'Afficher'}
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Cotisation et exercice sont enregistrés dans les paramètres du projet. Le solde et les moyens de paiement viennent de Ledgera.
              </p>
            </div>

            {financialSections.contributions && (
            <>
            {ledgerBalance && (
              <div className="rounded-lg border border-[#bcd4f6] bg-[#edf4ff]/60 p-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-medium text-[#113a5b] uppercase tracking-wide">Solde organisation (Ledgera)</p>
                  <p className="text-2xl font-bold text-[#0f3350]">
                    {Number(ledgerBalance.current_balance ?? 0).toLocaleString('fr-FR')}{' '}
                    <span className="text-base font-normal text-[#113a5b]">
                      {ledgerBalance.currency || settings.financial?.currency || 'RWF'}
                    </span>
                  </p>
                </div>
                {ledgerLoading && <span className="text-xs text-gray-500">Mise à jour…</span>}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">Montant de cotisation *</label>
                <input
                  type="number"
                  value={settings.financial?.contribution_amount || ''}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      financial: { ...(settings.financial || {}), contribution_amount: parseInt(e.target.value, 10) || 0 },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Fréquence de cotisation *</label>
                <select
                  value={settings.financial?.contribution_frequency || 'monthly'}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      financial: { ...(settings.financial || {}), contribution_frequency: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="monthly">Mensuelle</option>
                  <option value="quarterly">Trimestrielle</option>
                  <option value="yearly">Annuelle</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Devise (affichage projet) *</label>
                <select
                  value={settings.financial?.currency || 'RWF'}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      financial: { ...(settings.financial || {}), currency: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="BIF">BIF</option>
                  <option value="RWF">RWF</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Début d&apos;exercice fiscal</label>
                <input
                  type="text"
                  placeholder="ex. 01-01"
                  value={settings.financial?.fiscal_year_start || ''}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      financial: { ...(settings.financial || {}), fiscal_year_start: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Fin d&apos;exercice fiscal</label>
                <input
                  type="text"
                  placeholder="ex. 31-12"
                  value={settings.financial?.fiscal_year_end || ''}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      financial: { ...(settings.financial || {}), fiscal_year_end: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-amber-200/80 rounded-lg border border-amber-100 bg-amber-50/50 p-4">
              <h3 className="text-base font-semibold text-gray-900">Cotisations — facturation mensuelle (automatique)</h3>
              <p className="text-sm text-gray-600 mt-1 max-w-3xl">
                Paramètres pour la génération des périodes (1<sup>er</sup> de chaque mois), la notification des membres et
                les relances en cas de retard. L&apos;exécution technique (tâches planifiées) est côté plateforme ;
                enregistrez ici la politique du projet.
              </p>
              {(() => {
                const b = {
                  auto_create_on_month_start: true,
                  notify_members_on_new_period: true,
                  payment_deadline_day: 15,
                  arrears_warning_min_months: 3,
                  arrears_warning_max_months: 4,
                  arrears_notice_period_days: 15,
                  arrears_deactivation_notice_days: 15,
                  ...(settings.financial?.contribution_billing || {}),
                };
                const setB = (patch) =>
                  setSettings({
                    ...settings,
                    financial: {
                      ...(settings.financial || {}),
                      contribution_billing: { ...b, ...patch },
                    },
                  });
                return (
                  <div className="mt-4 space-y-4">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-gray-300"
                        checked={b.auto_create_on_month_start}
                        onChange={(e) => setB({ auto_create_on_month_start: e.target.checked })}
                      />
                      <span>
                        <span className="text-sm font-medium text-gray-900">Créer automatiquement chaque période le 1er du mois</span>
                        <span className="block text-xs text-gray-600">Une écriture de cotisation en attente est générée par membre actif (job plateforme).</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-gray-300"
                        checked={b.notify_members_on_new_period}
                        onChange={(e) => setB({ notify_members_on_new_period: e.target.checked })}
                      />
                      <span>
                        <span className="text-sm font-medium text-gray-900">Notifier tous les membres à l&apos;ouverture d&apos;une période</span>
                        <span className="block text-xs text-gray-600">Annonce (notification) lorsque la cotisation du mois est disponible.</span>
                      </span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Jour d&apos;échéance de paiement (mois en cours)</label>
                        <input
                          type="number"
                          min={1}
                          max={28}
                          value={b.payment_deadline_day}
                          onChange={(e) =>
                            setB({
                              payment_deadline_day: Math.min(28, Math.max(1, parseInt(e.target.value, 10) || 15)),
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">La page cotisation des membres affiche le délai restant (ex. avant le 15).</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Relance — retard (mois) min</label>
                        <input
                          type="number"
                          min={1}
                          max={12}
                          value={b.arrears_warning_min_months}
                          onChange={(e) =>
                            setB({ arrears_warning_min_months: Math.min(12, Math.max(1, parseInt(e.target.value, 10) || 3)) })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Relance — retard (mois) max</label>
                        <input
                          type="number"
                          min={1}
                          max={12}
                          value={b.arrears_warning_max_months}
                          onChange={(e) =>
                            setB({ arrears_warning_max_months: Math.min(12, Math.max(1, parseInt(e.target.value, 10) || 4)) })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">Entre ces seuils, un avertissement est prévu (campagne plateforme).</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Délai (jours) après avertissement</label>
                        <input
                          type="number"
                          min={1}
                          max={90}
                          value={b.arrears_notice_period_days}
                          onChange={(e) =>
                            setB({ arrears_notice_period_days: Math.max(1, parseInt(e.target.value, 10) || 15) })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">Si aucune action, notification d&apos;analyse (compte) selon règles org.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Délai (jours) annoncé avant désactivation</label>
                        <input
                          type="number"
                          min={1}
                          max={90}
                          value={b.arrears_deactivation_notice_days}
                          onChange={(e) =>
                            setB({ arrears_deactivation_notice_days: Math.max(1, parseInt(e.target.value, 10) || 15) })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200 rounded-lg border border-slate-100 bg-slate-50/50 p-4">
              <h3 className="text-base font-semibold text-gray-900">Cotisations — notifications in-app (membres)</h3>
              <p className="text-sm text-gray-600 mt-1 max-w-3xl">
                Contrôle des notifications in-app adressées au membre lorsque l&apos;état d&apos;une cotisation change
                (en attente, validée, rejetée). Indépendant de la relance périodique d&apos;ouverture de mois ci-dessus.
              </p>
              <div className="mt-3 space-y-2">
                {[
                  {
                    key: 'notify_member_pending',
                    label: 'Cotisation enregistrée en attente de validation',
                  },
                  {
                    key: 'notify_member_approved',
                    label: 'Cotisation approuvée / validée',
                  },
                  {
                    key: 'notify_member_rejected',
                    label: 'Cotisation rejetée',
                  },
                ].map((row) => {
                  const cn = { ...DEFAULT_FINANCIAL_NOTIFICATIONS, ...(settings.financial?.contribution_notifications || {}) };
                  return (
                    <label key={row.key} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                        checked={cn[row.key] !== false}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            financial: {
                              ...(settings.financial || {}),
                              contribution_notifications: { ...cn, [row.key]: e.target.checked },
                            },
                          })
                        }
                      />
                      {row.label}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Moyens de paiement (Ledgera)</h3>
                  <p className="text-sm text-gray-600">
                    Référencés par compte comptable. Le défaut est celui marqué « par défaut » dans Ledgera.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const firstAcc = ledgerAccounts.find((a) => a.is_active !== false);
                    setNewPaymentMethod({
                      name: '',
                      kind: 'virement',
                      ledger_account_id: firstAcc?.id || '',
                    });
                    setShowPaymentMethodModal(true);
                  }}
                  className="px-4 py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] text-sm whitespace-nowrap"
                >
                  + Ajouter un moyen
                </button>
              </div>
              {ledgerLoading && <p className="text-xs text-gray-500 mb-2">Chargement…</p>}
              <div className="space-y-3">
                {ledgerPaymentMethods.map((pm) => (
                  <div
                    key={pm.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-gray-200 rounded-lg"
                  >
                    <div>
                      <span className="text-sm font-medium text-gray-900">{pm.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {PAYMENT_KIND_LABELS[pm.kind] || pm.kind} · {ledgerAccountName(pm.ledger_account_id)}
                      </span>
                      {pm.is_default && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">Par défaut</span>
                      )}
                      {pm.is_active === false && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">Inactif</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {!pm.is_default && pm.is_active !== false && (
                        <button
                          type="button"
                          onClick={() => handleSetDefaultPaymentMethod(pm.id)}
                          className="text-sm text-[#113a5b] hover:text-[#0f3350]"
                        >
                          Définir par défaut
                        </button>
                      )}
                      {pm.is_active !== false && (
                        <button
                          type="button"
                          onClick={() => handleDeactivatePaymentMethod(pm.id)}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Désactiver
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {!ledgerLoading && ledgerPaymentMethods.length === 0 && (
                  <p className="text-sm text-gray-500 py-4">Aucun moyen de paiement dans Ledgera.</p>
                )}
              </div>
            </div>
            </>
            )}
          </div>
        )}

        {activeTab === 'assistance' && (
          <div className="space-y-4 md:space-y-6">
            <div>
              <h2 className="text-base md:text-xl font-bold text-[#0f3350]">Entraide & allocations</h2>
              <p className="mt-1 text-sm text-slate-600 max-w-2xl">
                Paramètres métier de gouvernance, cycle membre, mécanique financière, workflow des allocations et
                transparence.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 md:border-[#bcd4f6] bg-slate-50 md:bg-[#edf4ff]/50 p-3 md:p-5">
              <label className="block text-sm font-medium text-[#0f3350] mb-2">
                Montant de référence par défaut (FBU)
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={settings.assistance?.default_case_amount ?? 0}
                onChange={(e) => {
                  const raw = e.target.value;
                  const n = raw === '' ? 0 : Math.max(0, Number(raw) || 0);
                  setSettings({
                    ...settings,
                    assistance: { ...(settings.assistance || {}), default_case_amount: n },
                  });
                }}
                className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2.5 text-sm tabular-nums focus:border-[#bcd4f6] focus:outline-none focus:ring-2 focus:ring-[#2f6fd4]"
              />
              <p className="mt-3 text-xs text-slate-600">
                Exemple : si vous saisissez <span className="font-mono">150000</span>, chaque création de dossier
                enverra <span className="font-semibold text-[#113a5b]">150 000 FBU</span> comme montant demandé tant que
                ce paramètre n&apos;est pas modifié.
              </p>
            </div>

            <div className="flex flex-wrap gap-1.5 md:gap-2 -mx-0.5 px-0.5 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {ASSISTANCE_SUB_TABS.map((tab) => {
                const active = assistanceSubTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setAssistanceSubTab(tab.id);
                      setSettingsRouteTab('assistance', tab.id);
                    }}
                    className={[
                      'px-2 py-1 md:px-3 md:py-1.5 rounded-md md:rounded-lg text-xs md:text-sm border transition whitespace-nowrap flex-shrink-0',
                      active
                        ? 'bg-[#0f3350] md:bg-[#113a5b] text-white border-[#0f3350] md:border-[#113a5b]'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400 hover:text-[#0f3350] md:hover:border-[#bcd4f6] md:hover:text-[#113a5b]',
                    ].join(' ')}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {assistanceSubTab === 'governance' && (
              <section className="rounded-xl border border-slate-200 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-[#0f3350]">1. Gouvernance du mécanisme</h3>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Type de caisse</label>
                  <select
                    value={settings.assistance?.governance?.fund_type || 'solidarity'}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        assistance: {
                          ...(settings.assistance || {}),
                          governance: {
                            ...(settings.assistance?.governance || {}),
                            fund_type: e.target.value,
                          },
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="solidarity">Caisse de solidarité</option>
                    <option value="mutual">Mutuelle</option>
                    <option value="emergency">Caisse d&apos;urgence</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Règles d&apos;éligibilité</label>
                  <textarea
                    rows={3}
                    value={settings.assistance?.governance?.eligibility_rules || ''}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        assistance: {
                          ...(settings.assistance || {}),
                          governance: {
                            ...(settings.assistance?.governance || {}),
                            eligibility_rules: e.target.value,
                          },
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="Ex: minimum 6 mois d’ancienneté et statut actif."
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Périodicité des cotisations</label>
                    <select
                      value={settings.assistance?.governance?.contribution_periodicity || 'monthly'}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          assistance: {
                            ...(settings.assistance || {}),
                            governance: {
                              ...(settings.assistance?.governance || {}),
                              contribution_periodicity: e.target.value,
                            },
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    >
                      <option value="weekly">Hebdomadaire</option>
                      <option value="monthly">Mensuelle</option>
                      <option value="quarterly">Trimestrielle</option>
                      <option value="yearly">Annuelle</option>
                    </select>
                  </div>
                  <div />
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <label className="block text-xs text-slate-600 mb-2">
                    Catégories d’événements couvertes (avec seuils par catégorie)
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newAssistanceCategory}
                      onChange={(e) => setNewAssistanceCategory(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (editingAssistanceCategoryIndex !== null) {
                            handleUpdateAssistanceCategory();
                          } else {
                            handleAddAssistanceCategory();
                          }
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg"
                      placeholder={editingAssistanceCategoryIndex !== null ? 'Modifier la catégorie' : 'Nouvelle catégorie (ex: deces)'}
                    />
                    {editingAssistanceCategoryIndex !== null ? (
                      <>
                        <button
                          type="button"
                          onClick={handleUpdateAssistanceCategory}
                          className="px-3 py-2 bg-[#113a5b] text-white rounded-lg text-sm hover:bg-[#0f3350]"
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNewAssistanceCategory('');
                            setEditingAssistanceCategoryIndex(null);
                          }}
                          className="px-3 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm hover:bg-slate-300"
                        >
                          Annuler
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={handleAddAssistanceCategory}
                        className="px-3 py-2 bg-[#113a5b] text-white rounded-lg text-sm hover:bg-[#0f3350]"
                      >
                        Ajouter
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {getAssistanceCategoryThresholds().length > 0 ? (
                      getAssistanceCategoryThresholds().map((row, index) => (
                        <div key={`${row.category}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-800">{row.category}</span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditAssistanceCategory(index, row.category)}
                                className="text-sm text-[#113a5b] hover:underline"
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteAssistanceCategory(index)}
                                className="text-sm text-red-600 hover:underline"
                              >
                                Supprimer
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[11px] text-slate-600 mb-1">Seuil min (FBU)</label>
                              <input
                                type="number"
                                min={0}
                                value={row.min_amount ?? 0}
                                onChange={(e) =>
                                  handleUpdateAssistanceCategoryThreshold(index, 'min_amount', e.target.value)
                                }
                                className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] text-slate-600 mb-1">Seuil max (FBU)</label>
                              <input
                                type="number"
                                min={0}
                                value={row.max_amount ?? 0}
                                onChange={(e) =>
                                  handleUpdateAssistanceCategoryThreshold(index, 'max_amount', e.target.value)
                                }
                                className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-500">Aucune catégorie configurée.</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Seuil min allocation</label>
                    <input
                      type="number"
                      min={0}
                      value={settings.assistance?.governance?.assistance_min_amount ?? 0}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          assistance: {
                            ...(settings.assistance || {}),
                            governance: {
                              ...(settings.assistance?.governance || {}),
                              assistance_min_amount: Math.max(0, Number(e.target.value) || 0),
                            },
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Seuil max allocation</label>
                    <input
                      type="number"
                      min={0}
                      value={settings.assistance?.governance?.assistance_max_amount ?? 0}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          assistance: {
                            ...(settings.assistance || {}),
                            governance: {
                              ...(settings.assistance?.governance || {}),
                              assistance_max_amount: Math.max(0, Number(e.target.value) || 0),
                            },
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    />
                  </div>
                </div>
              </section>
            )}

            {assistanceSubTab === 'members' && (
              <section className="rounded-xl border border-slate-200 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-[#0f3350]">2. Vie des membres dans le mécanisme</h3>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Mode d&apos;adhésion</label>
                  <select
                    value={settings.assistance?.member_lifecycle?.membership_mode || 'open'}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        assistance: {
                          ...(settings.assistance || {}),
                          member_lifecycle: {
                            ...(settings.assistance?.member_lifecycle || {}),
                            membership_mode: e.target.value,
                          },
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="open">Adhésion libre</option>
                    <option value="approval_required">Adhésion avec validation</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Ancienneté minimale (mois)</label>
                    <input
                      type="number"
                      min={0}
                      value={settings.assistance?.member_lifecycle?.minimum_seniority_months ?? 0}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          assistance: {
                            ...(settings.assistance || {}),
                            member_lifecycle: {
                              ...(settings.assistance?.member_lifecycle || {}),
                              minimum_seniority_months: Math.max(0, Number(e.target.value) || 0),
                            },
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Situation cotisation</label>
                    <select
                      value={settings.assistance?.member_lifecycle?.contribution_situation_mode || 'auto'}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          assistance: {
                            ...(settings.assistance || {}),
                            member_lifecycle: {
                              ...(settings.assistance?.member_lifecycle || {}),
                              contribution_situation_mode: e.target.value,
                            },
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    >
                      <option value="auto">Calcul automatique</option>
                      <option value="manual">Saisie manuelle</option>
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.assistance?.member_lifecycle?.track_benefit_history)}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        assistance: {
                          ...(settings.assistance || {}),
                          member_lifecycle: {
                            ...(settings.assistance?.member_lifecycle || {}),
                            track_benefit_history: e.target.checked,
                          },
                        },
                      })
                    }
                    className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                  />
                  Historique de bénéfice activé
                </label>
              </section>
            )}

            {assistanceSubTab === 'finance' && (
              <section className="rounded-xl border border-slate-200 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-[#0f3350]">3. Mécanique financière métier</h3>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Cotisation attendue (FBU)</label>
                  <input
                    type="number"
                    min={0}
                    value={settings.assistance?.financial_mechanics?.expected_contribution_amount ?? 0}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        assistance: {
                          ...(settings.assistance || {}),
                          financial_mechanics: {
                            ...(settings.assistance?.financial_mechanics || {}),
                            expected_contribution_amount: Math.max(0, Number(e.target.value) || 0),
                          },
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.assistance?.financial_mechanics?.allow_arrears_tracking)}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        assistance: {
                          ...(settings.assistance || {}),
                          financial_mechanics: {
                            ...(settings.assistance?.financial_mechanics || {}),
                            allow_arrears_tracking: e.target.checked,
                          },
                        },
                      })
                    }
                    className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                  />
                  Suivre les retards de cotisation
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.assistance?.financial_mechanics?.exceptional_contribution_enabled)}
                    onChange={(e) => {
                      const en = e.target.checked;
                      setSettings((prev) => {
                        const fm = prev.assistance?.financial_mechanics || {};
                        let types = Array.isArray(fm.exceptional_contribution_types)
                          ? [...fm.exceptional_contribution_types]
                          : [];
                        const hasFilled = types.some(
                          (r) => String(r?.key || '').trim() && String(r?.label || '').trim()
                        );
                        if (en && !hasFilled) {
                          const leg =
                            String(fm.exceptional_contribution_label || 'Contribution exceptionnelle').trim() ||
                            'Contribution exceptionnelle';
                          types = [{ key: 'adhesion', label: leg }];
                        }
                        return {
                          ...prev,
                          assistance: {
                            ...(prev.assistance || {}),
                            financial_mechanics: {
                              ...fm,
                              exceptional_contribution_enabled: en,
                              exceptional_contribution_types: types,
                              exceptional_contribution_label:
                                types[0]?.label || fm.exceptional_contribution_label || 'Contribution exceptionnelle',
                            },
                          },
                        };
                      });
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                  />
                  Activer contribution exceptionnelle
                </label>
                {Boolean(settings.assistance?.financial_mechanics?.exceptional_contribution_enabled) && (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-slate-600 mb-2">
                        Types de contributions exceptionnelles (un libellé par ligne, clé technique unique — lettres,
                        chiffres, underscore)
                      </p>
                      <div className="space-y-2">
                        {(Array.isArray(settings.assistance?.financial_mechanics?.exceptional_contribution_types) &&
                        settings.assistance.financial_mechanics.exceptional_contribution_types.length > 0
                          ? settings.assistance.financial_mechanics.exceptional_contribution_types
                          : [{ key: 'adhesion', label: '' }]
                        ).map((row, idx) => (
                          <div
                            key={`exc-type-${idx}-${row.key || idx}`}
                            className="flex flex-col sm:flex-row gap-2 sm:items-end"
                          >
                            <div className="flex-1 min-w-0">
                              <label className="block text-[11px] text-slate-500 mb-0.5">Clé</label>
                              <input
                                type="text"
                                value={String(row?.key || '')}
                                onChange={(e) => {
                                  const k = normalizeExceptionalTypeKey(e.target.value);
                                  const list = [
                                    ...(settings.assistance?.financial_mechanics
                                      ?.exceptional_contribution_types || []),
                                  ];
                                  list[idx] = { ...list[idx], key: k, label: String(list[idx]?.label || '') };
                                  setSettings({
                                    ...settings,
                                    assistance: {
                                      ...(settings.assistance || {}),
                                      financial_mechanics: {
                                        ...(settings.assistance?.financial_mechanics || {}),
                                        exceptional_contribution_types: list,
                                        exceptional_contribution_label:
                                          list[0]?.label ||
                                          settings.assistance?.financial_mechanics?.exceptional_contribution_label,
                                      },
                                    },
                                  });
                                }}
                                className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono"
                                placeholder="ex. adhesion, formation"
                              />
                            </div>
                            <div className="flex-[2] min-w-0">
                              <label className="block text-[11px] text-slate-500 mb-0.5">Libellé affiché</label>
                              <input
                                type="text"
                                value={String(row?.label || '')}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const list = [
                                    ...(settings.assistance?.financial_mechanics
                                      ?.exceptional_contribution_types || []),
                                  ];
                                  list[idx] = { ...list[idx], label: v };
                                  setSettings({
                                    ...settings,
                                    assistance: {
                                      ...(settings.assistance || {}),
                                      financial_mechanics: {
                                        ...(settings.assistance?.financial_mechanics || {}),
                                        exceptional_contribution_types: list,
                                        exceptional_contribution_label: list[0]?.label || v,
                                      },
                                    },
                                  });
                                }}
                                className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm"
                                placeholder="Ex. Frais d'adhésion"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const list = [
                                  ...(settings.assistance?.financial_mechanics
                                    ?.exceptional_contribution_types || []),
                                ];
                                list.splice(idx, 1);
                                if (list.length === 0) {
                                  list.push({ key: 'nouveau_type', label: '' });
                                }
                                setSettings({
                                  ...settings,
                                  assistance: {
                                    ...(settings.assistance || {}),
                                    financial_mechanics: {
                                      ...(settings.assistance?.financial_mechanics || {}),
                                      exceptional_contribution_types: list,
                                      exceptional_contribution_label: list[0]?.label || 'Contribution exceptionnelle',
                                    },
                                  },
                                });
                              }}
                              className="px-2 py-1.5 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50 sm:mb-0.5"
                            >
                              Retirer
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const list = [
                              ...(settings.assistance?.financial_mechanics?.exceptional_contribution_types || []),
                            ];
                            const n = list.length + 1;
                            list.push({ key: `type_${n}`, label: '' });
                            setSettings({
                              ...settings,
                              assistance: {
                                ...(settings.assistance || {}),
                                financial_mechanics: {
                                  ...(settings.assistance?.financial_mechanics || {}),
                                  exceptional_contribution_types: list,
                                },
                              },
                            });
                          }}
                          className="text-sm font-medium text-[#113a5b] hover:underline"
                        >
                          + Ajouter un type
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      Chaque type donne un bouton sur la page Cotisations (une soumission en attente / validée par type et
                      par membre). Le premier libellé sert aussi de libellé par défaut pour d&apos;anciens écrans.
                    </p>
                  </div>
                )}
              </section>
            )}

            {assistanceSubTab === 'cases' && (
              <section className="rounded-xl border border-slate-200 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-[#0f3350]">4. Dossiers et événements</h3>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={settings.assistance?.case_workflow?.require_reason !== false}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        assistance: {
                          ...(settings.assistance || {}),
                          case_workflow: {
                            ...(settings.assistance?.case_workflow || {}),
                            require_reason: e.target.checked,
                          },
                        },
                      })
                    }
                    className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                  />
                  Motif obligatoire à la demande d&apos;aide
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={settings.assistance?.case_workflow?.require_decision_note !== false}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        assistance: {
                          ...(settings.assistance || {}),
                          case_workflow: {
                            ...(settings.assistance?.case_workflow || {}),
                            require_decision_note: e.target.checked,
                          },
                        },
                      })
                    }
                    className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                  />
                  Note de décision obligatoire
                </label>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Clôture auto après paiement (jours)</label>
                  <input
                    type="number"
                    min={0}
                    value={settings.assistance?.case_workflow?.close_after_days ?? 0}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        assistance: {
                          ...(settings.assistance || {}),
                          case_workflow: {
                            ...(settings.assistance?.case_workflow || {}),
                            close_after_days: Math.max(0, Number(e.target.value) || 0),
                          },
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>

                <div className="pt-3 mt-3 border-t border-slate-200 space-y-3">
                  <h4 className="text-sm font-semibold text-[#0f3350]">Notifications in-app (allocations)</h4>
                  <p className="text-xs text-slate-500">
                    Choisissez qui reçoit une notification dans l&apos;application à chaque étape du workflow des dossiers
                    d&apos;allocation.
                  </p>
                  {(() => {
                    const baseN = DEFAULT_ASSISTANCE_SETTINGS.case_workflow.notifications;
                    const n = { ...baseN, ...(settings.assistance?.case_workflow?.notifications || {}) };
                    const mode =
                      String(n.staff_delivery_mode || 'all_non_members').toLowerCase() === 'by_roles'
                        ? 'by_roles'
                        : 'all_non_members';
                    const staffRoles = Array.isArray(n.staff_roles) ? n.staff_roles : baseN.staff_roles;
                    const patchN = (partial) =>
                      setSettings({
                        ...settings,
                        assistance: {
                          ...(settings.assistance || {}),
                          case_workflow: {
                            ...(settings.assistance?.case_workflow || {}),
                            notifications: { ...n, ...partial },
                          },
                        },
                      });
                    return (
                      <>
                        <div>
                          <label className="block text-xs text-slate-600 mb-1">
                            Destinataires côté équipe (soumission, instruction)
                          </label>
                          <select
                            value={mode}
                            onChange={(e) => {
                              const v = e.target.value === 'by_roles' ? 'by_roles' : 'all_non_members';
                              patchN({
                                staff_delivery_mode: v,
                                staff_roles: v === 'by_roles' ? staffRoles : [],
                              });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                          >
                            <option value="all_non_members">Tous les comptes actifs (sauf rôle membre)</option>
                            <option value="by_roles">Rôles restreints (ci-dessous)</option>
                          </select>
                        </div>
                        {mode === 'by_roles' && (
                          <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                            <p className="text-xs text-slate-600 mb-2">Cochez les rôles à inclure (IAM / projet) :</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {NOTIFICATION_STAFF_ROLE_OPTIONS.map((opt) => {
                                const checked = staffRoles.includes(opt.value);
                                return (
                                  <label
                                    key={opt.value}
                                    className="flex items-center gap-2 text-sm text-slate-700"
                                  >
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                                      checked={checked}
                                      onChange={(e) => {
                                        const set = new Set(staffRoles);
                                        if (e.target.checked) set.add(opt.value);
                                        else set.delete(opt.value);
                                        patchN({ staff_roles: Array.from(set) });
                                      }}
                                    />
                                    {opt.label}
                                  </label>
                                );
                              })}
                            </div>
                            {staffRoles.length === 0 && (
                              <p className="text-xs text-amber-800 mt-2">
                                Aucun rôle sélectionné : aucune notification ne sera envoyée à l&apos;équipe pour les
                                événements « instruction ».
                              </p>
                            )}
                          </div>
                        )}

                        <div className="space-y-2 text-sm text-slate-700">
                          <p className="text-xs font-medium text-slate-600">Événements</p>
                          {[
                            {
                              key: 'on_case_created_member',
                              label: "Création / enregistrement d'un dossier (membre demandeur)",
                            },
                            { key: 'on_case_created_staff', label: 'Nouveau dossier en instruction (équipe)' },
                            { key: 'on_case_submitted_member', label: 'Soumission en instruction (membre demandeur)' },
                            { key: 'on_case_submitted_staff', label: 'Dossier soumis en instruction (équipe)' },
                            { key: 'on_case_approved_member', label: 'Demande approuvée (bénéficiaire)' },
                            { key: 'on_case_rejected_member', label: 'Demande non retenue (bénéficiaire)' },
                            { key: 'on_case_paid_member', label: 'Paiement enregistré (bénéficiaire)' },
                          ].map((row) => (
                            <label key={row.key} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                                checked={n[row.key] !== false}
                                onChange={(e) => patchN({ [row.key]: e.target.checked })}
                              />
                              {row.label}
                            </label>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </section>
            )}

            {assistanceSubTab === 'transparency' && (
              <section className="rounded-xl border border-slate-200 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-[#0f3350]">5. Transparence</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Rapports périodiques</label>
                    <select
                      value={settings.assistance?.transparency?.reports_frequency || 'monthly'}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          assistance: {
                            ...(settings.assistance || {}),
                            transparency: {
                              ...(settings.assistance?.transparency || {}),
                              reports_frequency: e.target.value,
                            },
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    >
                      <option value="weekly">Hebdomadaire</option>
                      <option value="monthly">Mensuel</option>
                      <option value="quarterly">Trimestriel</option>
                      <option value="yearly">Annuel</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={settings.assistance?.transparency?.enable_aid_summary !== false}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          assistance: {
                            ...(settings.assistance || {}),
                            transparency: {
                              ...(settings.assistance?.transparency || {}),
                              enable_aid_summary: e.target.checked,
                            },
                          },
                        })
                      }
                      className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                    />
                    Synthèse des aides
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={settings.assistance?.transparency?.enable_contributions_summary !== false}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          assistance: {
                            ...(settings.assistance || {}),
                            transparency: {
                              ...(settings.assistance?.transparency || {}),
                              enable_contributions_summary: e.target.checked,
                            },
                          },
                        })
                      }
                      className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                    />
                    Synthèse des contributions
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={settings.assistance?.transparency?.enable_case_tracking !== false}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          assistance: {
                            ...(settings.assistance || {}),
                            transparency: {
                              ...(settings.assistance?.transparency || {}),
                              enable_case_tracking: e.target.checked,
                            },
                          },
                        })
                      }
                      className="h-4 w-4 rounded border-slate-300 text-[#113a5b]"
                    />
                    Suivi des cas
                  </label>
                </div>
              </section>
            )}
          </div>
        )}

        {activeTab === 'system' && (
          <div className="space-y-4 md:space-y-8">
            {process.env.REACT_APP_ADMIN_CONSOLE_URL ? (
              <div className="rounded-xl border border-slate-200 md:border-[#bcd4f6] bg-slate-50 md:bg-[#edf4ff]/60 p-3 md:p-5">
                <h2 className="text-sm md:text-base font-bold text-[#0f3350] mb-2">Console d&apos;administration (plateforme)</h2>
                <p className="text-xs md:text-sm text-[#113a5b] mb-3">
                  Projets, apps activées, IAM avancé, clés et métadonnées d&apos;organisation se gèrent souvent depuis la console Uruganda, pas uniquement depuis Entrea.
                </p>
                <a
                  href={process.env.REACT_APP_ADMIN_CONSOLE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-[#113a5b] text-white rounded-lg text-sm font-medium hover:bg-[#0f3350]"
                >
                  Ouvrir la console
                </a>
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 md:p-4 text-xs md:text-sm text-gray-600">
                Pour lier un raccourci vers la console admin, définissez <code className="bg-white px-1 rounded">REACT_APP_ADMIN_CONSOLE_URL</code> dans l&apos;environnement du frontend Entrea.
              </div>
            )}

              <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 md:p-6">
              <h2 className="text-base md:text-xl font-bold text-[#0f3350] mb-2">Sécurité utilisateur (IAM)</h2>
              <p className="text-xs sm:text-sm text-slate-600 mb-4">
                Le PIN et les secrets d&apos;authentification sont désormais gérés côté IAM / console admin pour assurer une gouvernance centralisée.
              </p>
              <div className="flex flex-wrap gap-2">
                {user?.id && (
                  <button
                    type="button"
                    onClick={() => navigate(`/users/${user.id}`)}
                    className="px-4 py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition text-sm"
                  >
                    Ouvrir mon profil IAM
                  </button>
                )}
                {ADMIN_CONSOLE_URL && (
                  <a
                    href={adminConsoleUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                  >
                    Ouvrir la console admin
                  </a>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center mb-4">
            <h2 className="text-base md:text-xl font-bold text-[#0f3350]">Paramètres Système</h2>
              <button
                onClick={() => navigate('/modules')}
                className="px-4 py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition font-medium text-sm flex items-center gap-2"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
                Applications & modules
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">Fuseau horaire</label>
                <select
                  value={settings.system?.timezone || 'Africa/Kigali'}
                  onChange={(e) => setSettings({
                    ...settings,
                    system: { ...(settings.system || {}), timezone: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Africa/Kigali">Africa/Kigali</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Format de date</label>
                <select
                  value={settings.system?.date_format || 'DD/MM/YYYY'}
                  onChange={(e) => setSettings({
                    ...settings,
                    system: { ...(settings.system || {}), date_format: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Langue</label>
                <select
                  value={settings.system?.language || 'fr'}
                  onChange={(e) => setSettings({
                    ...settings,
                    system: { ...(settings.system || {}), language: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.system?.email_notifications || false}
                  onChange={(e) => setSettings({
                    ...settings,
                    system: { ...(settings.system || {}), email_notifications: e.target.checked }
                  })}
                  className="w-4 h-4 text-[#113a5b] border-gray-300 rounded focus:ring-[#2f6fd4]"
                />
                <label className="ml-2 text-sm text-gray-700">Notifications par email</label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'events' && (
          <div className="space-y-4 md:space-y-6">
            <h2 className="text-base md:text-xl font-bold text-[#0f3350]">Configuration des Événements</h2>
            
            {/* Types d'événements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm md:text-lg font-semibold text-gray-900">Types d'événements</h3>
                <button
                  onClick={() => {
                    setNewEventType('');
                    setEditingEventTypeIndex(null);
                    document.getElementById('eventTypeInput')?.focus();
                  }}
                  className="px-3 py-1.5 text-sm bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition"
                >
                  + Ajouter
                </button>
              </div>
              
              {/* Formulaire d'ajout/modification */}
              <div className="mb-4 flex gap-2">
                <input
                  id="eventTypeInput"
                  type="text"
                  value={newEventType}
                  onChange={(e) => setNewEventType(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (editingEventTypeIndex !== null) {
                        handleUpdateEventType();
                      } else {
                        handleAddEventType();
                      }
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={editingEventTypeIndex !== null ? "Modifier le type" : "Nouveau type d'événement"}
                />
                {editingEventTypeIndex !== null ? (
                  <>
                    <button
                      onClick={handleUpdateEventType}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => {
                        setNewEventType('');
                        setEditingEventTypeIndex(null);
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition text-sm"
                    >
                      Annuler
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleAddEventType}
                      className="px-4 py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition text-sm"
                  >
                    Ajouter
                  </button>
                )}
              </div>

              {/* Liste des types */}
              <div className="space-y-2">
                {Array.isArray(settings.events?.event_types) && settings.events.event_types.length > 0 ? (
                  settings.events.event_types.map((type, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-sm text-gray-900">{type}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditEventType(index, type)}
                          className="p-1.5 text-[#113a5b] hover:bg-[#edf4ff] rounded transition"
                          title="Modifier"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteEventType(index)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">Aucun type d'événement défini</p>
                )}
              </div>
            </div>

            {/* Modes de réunion */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm md:text-lg font-semibold text-gray-900">Modes de réunion</h3>
                <button
                  onClick={() => {
                    setNewMeetingMode('');
                    setEditingMeetingModeIndex(null);
                    document.getElementById('meetingModeInput')?.focus();
                  }}
                  className="px-3 py-1.5 text-sm bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition"
                >
                  + Ajouter
                </button>
              </div>
              
              {/* Formulaire d'ajout/modification */}
              <div className="mb-4 flex gap-2">
                <input
                  id="meetingModeInput"
                  type="text"
                  value={newMeetingMode}
                  onChange={(e) => setNewMeetingMode(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (editingMeetingModeIndex !== null) {
                        handleUpdateMeetingMode();
                      } else {
                        handleAddMeetingMode();
                      }
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={editingMeetingModeIndex !== null ? "Modifier le mode" : "Nouveau mode de réunion"}
                />
                {editingMeetingModeIndex !== null ? (
                  <>
                    <button
                      onClick={handleUpdateMeetingMode}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => {
                        setNewMeetingMode('');
                        setEditingMeetingModeIndex(null);
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition text-sm"
                    >
                      Annuler
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleAddMeetingMode}
                    className="px-4 py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition text-sm"
                  >
                    Ajouter
                  </button>
                )}
              </div>

              {/* Liste des modes */}
              <div className="space-y-2">
                {Array.isArray(settings.events?.meeting_modes) && settings.events.meeting_modes.length > 0 ? (
                  settings.events.meeting_modes.map((mode, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-sm text-gray-900">{mode}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditMeetingMode(index, mode)}
                          className="p-1.5 text-[#113a5b] hover:bg-[#edf4ff] rounded transition"
                          title="Modifier"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteMeetingMode(index)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">Aucun mode de réunion défini</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">Type d'événement par défaut</label>
                <select
                  value={settings.events?.default_event_type || 'meeting'}
                  onChange={(e) => setSettings({
                    ...settings,
                    events: { ...(settings.events || {}), default_event_type: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {(Array.isArray(settings.events?.event_types) ? settings.events.event_types : ['meeting', 'deadline', 'event']).map(type => (
                    <option key={type} value={type}>
                      {type === 'meeting' ? 'Réunion' : type === 'deadline' ? 'Échéance' : type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Mode de réunion par défaut</label>
                <select
                  value={settings.events?.default_meeting_mode || 'présentiel'}
                  onChange={(e) => setSettings({
                    ...settings,
                    events: { ...(settings.events || {}), default_meeting_mode: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {(Array.isArray(settings.events?.meeting_modes) ? settings.events.meeting_modes : ['présentiel', 'en ligne', 'hybride']).map(mode => (
                    <option key={mode} value={mode}>{mode}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bank Account Modal */}
      {showBankModal && (
        <ModalShell
          maxWidthClass="max-w-md"
          onBackdropClick={() => {
            setShowBankModal(false);
            resetBankForm();
          }}
        >
          <div className="relative p-4 sm:p-6">
            <div className="relative">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-[#0f3350]">
                  Coordonnées bancaires (Ledgera)
                </h3>
                <button
                  onClick={() => {
                    setShowBankModal(false);
                    resetBankForm();
                  }}
                  className="text-gray-400 hover:text-gray-500 active:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full hover:bg-white md:bg-transparent"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Compte comptable banque (Ledgera) *</label>
                  <select
                    value={newBankAccount.ledger_account_id}
                    onChange={(e) =>
                      setNewBankAccount({ ...newBankAccount, ledger_account_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">— Sélectionner —</option>
                    {banqueLedgerAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                  {banqueLedgerAccounts.length === 0 && (
                    <p className="text-xs text-amber-800 mt-1">
                      Créez d&apos;abord un compte de type banque via « Compte comptable banque ».
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nom de la banque *</label>
                  <input
                    type="text"
                    value={newBankAccount.bank_name}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, bank_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Titulaire (libellé)</label>
                  <input
                    type="text"
                    value={newBankAccount.account_holder}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, account_holder: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Optionnel"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Numéro de compte</label>
                  <input
                    type="text"
                    value={newBankAccount.account_number}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, account_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">IBAN</label>
                  <input
                    type="text"
                    value={newBankAccount.iban}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, iban: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">BIC / SWIFT</label>
                  <input
                    type="text"
                    value={newBankAccount.swift_code}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, swift_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Agence</label>
                  <input
                    type="text"
                    value={newBankAccount.branch}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, branch: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4 sm:mt-6">
                <button
                  onClick={handleAddBankAccount}
                  className="flex-1 px-4 py-2.5 min-h-[44px] bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] text-sm md:text-base"
                >
                  Ajouter
                </button>
                <button
                  onClick={() => {
                    setShowBankModal(false);
                    resetBankForm();
                  }}
                  className="flex-1 px-4 py-2.5 min-h-[44px] bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm md:text-base"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      {/* Signatory Modal */}
      {showSignatoryModal && (
        <ModalShell
          maxWidthClass="max-w-md"
          onBackdropClick={() => {
            setShowSignatoryModal(false);
            resetSignatoryForm();
          }}
        >
          <div className="relative p-4 sm:p-6">
            <div className="relative">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-[#0f3350]">Ajouter un signataire</h3>
                <button
                  onClick={() => {
                    setShowSignatoryModal(false);
                    resetSignatoryForm();
                  }}
                  className="text-gray-400 hover:text-gray-500 active:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full hover:bg-white md:bg-transparent"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Membre *</label>
                  <select
                    value={newSignatory.member_id || ''}
                    onChange={(e) => handleMemberSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Sélectionnez un membre</option>
                    {members.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} {member.email ? `(${member.email})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Le signataire doit être un membre de l'association</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nom *</label>
                  <input
                    type="text"
                    value={newSignatory.name}
                    onChange={(e) => setNewSignatory({ ...newSignatory, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    required
                    disabled={!!newSignatory.member_id}
                    readOnly={!!newSignatory.member_id}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Rôle *</label>
                  <input
                    type="text"
                    value={newSignatory.role}
                    onChange={(e) => setNewSignatory({ ...newSignatory, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    placeholder="Ex: Président, Trésorier, Secrétaire"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={newSignatory.phone || ''}
                    onChange={(e) => setNewSignatory({ ...newSignatory, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    disabled={!!newSignatory.member_id}
                    readOnly={!!newSignatory.member_id}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    value={newSignatory.email || ''}
                    onChange={(e) => setNewSignatory({ ...newSignatory, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    disabled={!!newSignatory.member_id}
                    readOnly={!!newSignatory.member_id}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4 sm:mt-6">
                <button
                  onClick={handleAddSignatory}
                  className="flex-1 px-4 py-2.5 min-h-[44px] bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] text-sm md:text-base"
                >
                  Ajouter
                </button>
                <button
                  onClick={() => {
                    setShowSignatoryModal(false);
                    resetSignatoryForm();
                  }}
                  className="flex-1 px-4 py-2.5 min-h-[44px] bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm md:text-base"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      {showLedgerAccountModal && (
        <ModalShell
          maxWidthClass="max-w-md"
          onBackdropClick={() => {
            setShowLedgerAccountModal(false);
            setNewLedgerAccount({ name: '', currency: 'RWF', account_type: 'banque' });
          }}
        >
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Nouveau compte comptable (Ledgera)</h3>
              <button
                type="button"
                onClick={() => {
                  setShowLedgerAccountModal(false);
                  setNewLedgerAccount({ name: '', currency: 'RWF', account_type: 'banque' });
                }}
                className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Fermer"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Nom du compte *</label>
                <input
                  type="text"
                  value={newLedgerAccount.name}
                  onChange={(e) => setNewLedgerAccount({ ...newLedgerAccount, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="ex. Banque principale"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type de compte *</label>
                <select
                  value={newLedgerAccount.account_type || 'banque'}
                  onChange={(e) =>
                    setNewLedgerAccount({ ...newLedgerAccount, account_type: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="caisse">Caisse (espèces)</option>
                  <option value="banque">Banque</option>
                  <option value="wallet">Wallet</option>
                  <option value="contribution_pool">Pool cotisations</option>
                  <option value="fonds">Fonds</option>
                  <option value="operationnel">Opérationnel</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Devise</label>
                <select
                  value={newLedgerAccount.currency}
                  onChange={(e) => setNewLedgerAccount({ ...newLedgerAccount, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="RWF">RWF</option>
                  <option value="BIF">BIF</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={handleCreateLedgerBankAccount}
                className="flex-1 px-4 py-2.5 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350]"
              >
                Créer
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLedgerAccountModal(false);
                  setNewLedgerAccount({ name: '', currency: 'RWF', account_type: 'banque' });
                }}
                className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {showPaymentMethodModal && (
        <ModalShell
          maxWidthClass="max-w-md"
          onBackdropClick={() => {
            setShowPaymentMethodModal(false);
            setNewPaymentMethod({ name: '', kind: 'virement', ledger_account_id: '' });
          }}
        >
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Moyen de paiement (Ledgera)</h3>
              <button
                type="button"
                onClick={() => {
                  setShowPaymentMethodModal(false);
                  setNewPaymentMethod({ name: '', kind: 'virement', ledger_account_id: '' });
                }}
                className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Fermer"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Libellé *</label>
                <input
                  type="text"
                  value={newPaymentMethod.name}
                  onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="ex. Virement BOA"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={newPaymentMethod.kind}
                  onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, kind: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {paymentKindOptions.map((k) => (
                    <option key={k} value={k}>
                      {PAYMENT_KIND_LABELS[k] || k}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Compte Ledgera *</label>
                <select
                  value={newPaymentMethod.ledger_account_id}
                  onChange={(e) =>
                    setNewPaymentMethod({ ...newPaymentMethod, ledger_account_id: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">— Sélectionner —</option>
                  {ledgerAccounts
                    .filter((a) => a.is_active !== false)
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                </select>
                {ledgerAccounts.filter((a) => a.is_active !== false).length === 0 && (
                  <p className="text-xs text-amber-800 mt-1">Créez d&apos;abord un compte dans Ledgera.</p>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={handleAddLedgerPaymentMethod}
                className="flex-1 px-4 py-2.5 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350]"
              >
                Ajouter
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPaymentMethodModal(false);
                  setNewPaymentMethod({ name: '', kind: 'virement', ledger_account_id: '' });
                }}
                className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default Settings;

